import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getWorkbooks, getCourseworkStatus, submitCoursework, getQuestions } from '../lib/api';
import formatApiError from '../lib/formatApiError';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Books,
  SignOut,
  CheckCircle,
  Circle,
  Clock,
  ArrowRight,
  User,
  Lock,
  Warning,
  PaperPlaneTilt,
  Spinner
} from '@phosphor-icons/react';

// Coursework status banners (spec §7 PR 8a B4, owner-editable constants).
// NO pass/fail language anywhere on the learner side (owner ruling).
const COURSEWORK_BANNERS = {
  submitted:
    "Coursework submitted — awaiting your assessor's review. Your answers are locked while the review takes place.",
  returned:
    'Your assessor has returned your coursework. Revisit the highlighted questions, then submit again.',
  approved:
    'Your coursework review is complete and no further action is needed from you at this stage. Your assessor will confirm your overall qualification result once all requirements have been met.'
};

const isWorkbookComplete = (workbook) =>
  workbook.total_questions === 0 || workbook.completed_questions >= workbook.total_questions;

const isAcceptedStatus = (status) => status === 'accepted' || status === 'accepted_with_feedback';

// PR 8a.2 item 3: the return-time reopened counts never shrink as the learner
// fixes questions, so recount from the live per-question statuses (the same
// GET /questions the workbook view reads) and show only the questions still
// outstanding — the notice clears at zero. Returns null when not returned.
const computeOutstandingByWorkbook = async (courseworkData) => {
  if (courseworkData?.status !== 'returned') return null;
  const reopenedIds = courseworkData.reopened_question_ids || [];
  const counts = {};
  await Promise.all(
    Object.keys(courseworkData.reopened_by_workbook || {}).map(async (workbookId) => {
      const response = await getQuestions(workbookId);
      counts[workbookId] = response.data.filter(
        (question) => reopenedIds.includes(question._id) && !isAcceptedStatus(question.status)
      ).length;
    })
  );
  return counts;
};

// Sequential unit locking (PR 8a B1) — UI ONLY, no backend enforcement (owner
// ruling: backend enforcement would break QA matrix runs). The dashboard has
// no explicit ordering field, so units are ordered by title (Unit 1..Unit 5);
// only coursework-included workbooks join the chain — excluded (sandbox)
// workbooks are never locked and never block.
const sortByTitle = (workbooks) =>
  [...workbooks].sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true }));

export default function LearnerDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [workbooks, setWorkbooks] = useState([]);
  const [coursework, setCoursework] = useState(null);
  const [outstandingByWorkbook, setOutstandingByWorkbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [declarationTicked, setDeclarationTicked] = useState(false);
  const [submittingCoursework, setSubmittingCoursework] = useState(false);
  const [courseworkError, setCourseworkError] = useState('');
  const [courseworkJustSubmitted, setCourseworkJustSubmitted] = useState(false);

  useEffect(() => {
    loadWorkbooks();
  }, []);

  const loadWorkbooks = async () => {
    try {
      const response = await getWorkbooks();
      setWorkbooks(response.data);
    } catch (error) {
      console.error('Failed to load workbooks:', error);
    }
    try {
      const courseworkRes = await getCourseworkStatus();
      setCoursework(courseworkRes.data);
      try {
        setOutstandingByWorkbook(await computeOutstandingByWorkbook(courseworkRes.data));
      } catch (error) {
        // Fall back to the return-time counts rather than hiding the notice.
        console.error('Failed to load outstanding question counts:', error);
        setOutstandingByWorkbook(null);
      }
    } catch (error) {
      console.error('Failed to load coursework status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSubmitCoursework = async () => {
    if (!declarationTicked || submittingCoursework) return;
    setCourseworkError('');
    setSubmittingCoursework(true);
    try {
      await submitCoursework(true);
      setDeclarationTicked(false);
      setCourseworkJustSubmitted(true);
      await loadWorkbooks();
    } catch (error) {
      console.error('Failed to submit coursework:', error);
      setCourseworkError(formatApiError(error));
    } finally {
      setSubmittingCoursework(false);
    }
  };

  const getProgressPercentage = (completed, total) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const getWorkbookStatus = (completed, total) => {
    if (completed === 0) return { label: 'Not Started', color: 'status-not-started' };
    if (completed === total) return { label: 'Completed', color: 'status-accepted' };
    return { label: 'In Progress', color: 'status-draft' };
  };

  const sortedWorkbooks = sortByTitle(workbooks);
  const includedOrder = sortedWorkbooks.filter((wb) => wb.included_in_coursework !== false);
  // A card locks when ANY earlier included unit is incomplete (chain rule).
  const lockInfoById = {};
  includedOrder.forEach((wb, index) => {
    const previousIncomplete = includedOrder.slice(0, index).some((earlier) => !isWorkbookComplete(earlier));
    lockInfoById[wb._id] = {
      locked: previousIncomplete,
      previousTitle: index > 0 ? includedOrder[index - 1].title : null,
      nextTitle: index < includedOrder.length - 1 ? includedOrder[index + 1].title : null
    };
  });

  const courseworkStatus = coursework?.status || null;
  // PR 8a.2 item 4: while the coursework is with the assessor every question
  // is read-only (backend 423 is the enforcement) — the card button should
  // say so instead of promising "Continue".
  const courseworkFrozen = courseworkStatus === 'submitted' || courseworkStatus === 'approved';
  const banner = courseworkStatus ? COURSEWORK_BANNERS[courseworkStatus] : null;
  const showSubmitPanel =
    !!coursework?.complete && (courseworkStatus === null || courseworkStatus === 'returned');
  const reopenedByWorkbook = coursework?.reopened_by_workbook || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="learner-dashboard">
      {/* Header */}
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <img
                src="/innova-logo.png"
                alt="Innova Training"
                className="h-10 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <User size={20} weight="duotone" />
                <span className="text-sm font-medium">{user?.name}</span>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <SignOut size={20} className="mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Level 2 Spectator Safety Coursework</h1>
          <p className="text-slate-500 mt-1">Complete the units below to finish your coursework.</p>
        </div>

        {/* Coursework status banner (PR 8a B4) */}
        {banner && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              courseworkStatus === 'returned'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
            data-testid={`coursework-banner-${courseworkStatus}`}
          >
            <p className="text-sm font-medium">{banner}</p>
          </div>
        )}

        {/* Submit Coursework panel (PR 8a B2/B5): shown only when every
            included unit is complete and the coursework is not currently
            with the assessor. Checkbox declaration only — no typed name. */}
        {!loading && showSubmitPanel && (
          <div className="mb-8 card p-6 border-2 border-[#3B5A7A]/30" data-testid="submit-coursework-panel">
            <div className="flex items-center gap-3 mb-3">
              <PaperPlaneTilt size={24} weight="duotone" className="text-[#3B5A7A]" />
              <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">
                {courseworkStatus === 'returned' ? 'Resubmit your coursework' : 'Submit your coursework'}
              </h2>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              All units are complete. Read the declaration below, tick the box to confirm, and submit your
              coursework to your assessor.
            </p>
            <div className="flex items-start gap-3 p-4 rounded-md bg-slate-50 border border-slate-200 mb-4">
              <Checkbox
                id="coursework-declaration"
                checked={declarationTicked}
                onCheckedChange={(checked) => setDeclarationTicked(checked === true)}
                data-testid="coursework-declaration-checkbox"
              />
              <label htmlFor="coursework-declaration" className="text-sm text-slate-700 cursor-pointer">
                {coursework?.declaration_text}
              </label>
            </div>
            {courseworkError && (
              <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="coursework-submit-error">
                <div className="flex items-start gap-2">
                  <Warning size={18} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">{courseworkError}</p>
                </div>
              </div>
            )}
            <Button
              onClick={handleSubmitCoursework}
              disabled={!declarationTicked || submittingCoursework}
              className="bg-[#3B5A7A] hover:bg-[#2A4158] text-white"
              data-testid="submit-coursework-btn"
            >
              {submittingCoursework ? (
                <>
                  <Spinner size={18} className="animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={18} className="mr-2" />
                  Submit Coursework
                </>
              )}
            </Button>
          </div>
        )}
        {courseworkJustSubmitted && courseworkStatus === 'submitted' && (
          <div className="mb-8 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800" data-testid="coursework-submitted-confirmation">
            Your coursework has been submitted to your assessor.
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B5A7A]"></div>
          </div>
        ) : sortedWorkbooks.length === 0 ? (
          <div className="card p-12 text-center">
            <Books size={48} weight="duotone" className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No Workbooks Available</h3>
            <p className="text-slate-500">Your assigned workbooks will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sortedWorkbooks.map((workbook) => {
              const progress = getProgressPercentage(workbook.completed_questions, workbook.total_questions);
              const status = getWorkbookStatus(workbook.completed_questions, workbook.total_questions);
              const description = workbook.description?.trim();
              const lockInfo = lockInfoById[workbook._id] || { locked: false, previousTitle: null, nextTitle: null };
              const complete = isWorkbookComplete(workbook) && workbook.total_questions > 0;
              const reopenedCount = reopenedByWorkbook[workbook._id] || 0;
              // Live outstanding count (PR 8a.2 item 3); the return-time count
              // is the fallback until the live statuses have loaded.
              const actionNeededCount = outstandingByWorkbook
                ? (outstandingByWorkbook[workbook._id] || 0)
                : reopenedCount;

              return (
                <div key={workbook._id} className="card overflow-hidden animate-fade-in" data-testid={`workbook-card-${workbook._id}`}>
                  <div className={`p-6 ${lockInfo.locked ? 'opacity-70' : ''}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900 font-['Manrope'] mb-1">
                          {workbook.title}
                        </h3>
                        {description && (
                          <p className="text-sm text-slate-500 line-clamp-2">
                            {description}
                          </p>
                        )}
                      </div>
                      {lockInfo.locked && (
                        <Lock size={20} weight="duotone" className="text-slate-400 flex-shrink-0" data-testid={`workbook-locked-icon-${workbook._id}`} />
                      )}
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <span className={`status-badge ${status.color}`}>
                        {status.label === 'Completed' ? (
                          <CheckCircle size={14} weight="bold" className="mr-1" />
                        ) : status.label === 'In Progress' ? (
                          <Clock size={14} weight="bold" className="mr-1" />
                        ) : (
                          <Circle size={14} weight="bold" className="mr-1" />
                        )}
                        {status.label}
                      </span>
                    </div>

                    {/* Returned coursework: units with reopened questions need action (PR 8a B4).
                        PR 8a.2 item 3: count only the questions still outstanding, clearing at zero. */}
                    {courseworkStatus === 'returned' && actionNeededCount > 0 && (
                      <div className="mb-4 p-2 rounded-md bg-amber-50 border border-amber-200" data-testid={`workbook-action-needed-${workbook._id}`}>
                        <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                          <Warning size={14} weight="fill" className="text-amber-500" />
                          Action needed — {actionNeededCount} question(s) to revisit
                        </p>
                      </div>
                    )}

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-medium text-slate-900">
                          {workbook.completed_questions} / {workbook.total_questions} questions
                        </span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>

                    {/* Unit complete line (PR 8a B1). */}
                    {complete && (
                      <p className="text-sm font-medium text-emerald-700 mb-3" data-testid={`workbook-complete-${workbook._id}`}>
                        Unit complete ✓{lockInfo.nextTitle ? ` — continue to ${lockInfo.nextTitle}` : ''}
                      </p>
                    )}

                    {lockInfo.locked ? (
                      <Button
                        className="w-full bg-slate-200 text-slate-500 cursor-not-allowed hover:bg-slate-200"
                        disabled
                        data-testid={`start-workbook-${workbook._id}`}
                      >
                        <Lock size={18} className="mr-2" />
                        {lockInfo.previousTitle ? `Unlocks after ${lockInfo.previousTitle}` : 'Locked'}
                      </Button>
                    ) : (
                      <Link to={`/workbook/${workbook._id}`}>
                        <Button className="w-full bg-[#3B5A7A] hover:bg-[#2A4158]" data-testid={`start-workbook-${workbook._id}`}>
                          {courseworkFrozen ? 'View answers' : status.label === 'Not Started' ? 'Start Workbook' : 'Continue'}
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
